import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Briefcase, TrendingUp, AlertTriangle, Building2 } from "lucide-react";
import { VERTICALS } from "@/lib/verticals";
import { listCaseStudies } from "@/lib/api";
import CaseCard from "@/components/CaseCard";
import { VERTICAL } from "@/constants/testIds";
import Seo, { serviceJsonLd, breadcrumbJsonLd } from "@/components/Seo";

export default function Verticale() {
  const { vertical } = useParams();
  const v = VERTICALS[vertical];

  if (!v) return <Navigate to="/per-chi-e" replace />;
  return (
    <>
      <Seo
        title={`${v.seo_title || v.titolo || v.nome} | Lovac`}
        description={v.meta_description || `Lovac trova ${(v.nome || "").toLowerCase()} attraverso head hunting attivo, database proprietario e recruiter specializzati.`}
        path={`/per-chi-e/${vertical}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            serviceJsonLd({
              name: v.seo_title || v.titolo || v.nome,
              description: v.meta_description || v.sottotitolo || "",
              path: `/per-chi-e/${vertical}`,
            }),
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Per chi è", path: "/per-chi-e" },
              { name: v.nome || v.titolo, path: `/per-chi-e/${vertical}` },
            ]),
          ],
        }}
      />
      {v.rich ? <RichVertical v={v} /> : <LegacyVertical v={v} />}
    </>
  );
}

/* =========================================
   LAYOUT RICCO, per verticali con copy esteso
   ========================================= */
function RichVertical({ v }) {
  const [relatedCases, setRelatedCases] = useState([]);

  useEffect(() => {
    if (!v.casi_studio_slugs?.length) return;
    listCaseStudies()
      .then((all) => setRelatedCases(all.filter((c) => v.casi_studio_slugs.includes(c.slug))))
      .catch(() => {});
  }, [v]);

  const problemIcons = [Briefcase, AlertTriangle, TrendingUp];

  return (
    <>
      {/* 1. HERO */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-24 pb-20 relative z-10">
          <span className="badge-on-dark">{v.hero_eyebrow}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 text-on-dark max-w-4xl leading-[1.05]">
            {v.seo_title}
          </h1>
          <p className="text-on-dark-muted text-lg mt-7 max-w-2xl leading-relaxed">{v.subtitle}</p>
        </div>
      </section>

      {/* 2. IL MERCATO */}
      {v.market && (
        <section className="py-24 bg-lovac">
          <div className="container-lovac">
            <div className="max-w-3xl">
              <p className="badge-lovac">I numeri del mercato</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
                {v.market.title}
              </h2>
              <p className="text-lovac-muted mt-6 text-base leading-relaxed">{v.market.intro}</p>
            </div>

            {/* 3 numeri chiave */}
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {v.market.numbers.map((n) => (
                <div
                  key={n.v}
                  className="rounded-xl p-8 flex flex-col h-full"
                  style={{
                    background: "var(--lovac-bg-2)",
                    border: "0.5px solid var(--lovac-border)",
                  }}
                >
                  <p className="font-display text-6xl text-lovac-accent leading-none">{n.v}</p>
                  <p className="text-base text-lovac-dark mt-5 leading-relaxed flex-1">{n.l}</p>
                  <p className="text-[11px] text-lovac-muted italic mt-5 pt-4 border-t border-lovac">
                    Fonte: {n.src}
                  </p>
                </div>
              ))}
            </div>

            {/* RAL panel */}
            {v.market.ral && (
              <div className="mt-12 card-dark">
                <div className="flex items-center gap-3 mb-5">
                  <TrendingUp size={18} className="text-lovac-accent" />
                  <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">RAL di mercato</p>
                </div>
                <h3 className="font-display text-2xl text-on-dark leading-tight max-w-3xl">
                  {v.market.ral.title}
                </h3>
                <div className={`mt-10 grid grid-cols-1 ${
                  v.market.ral.rows.length >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" :
                  v.market.ral.rows.length === 3 ? "md:grid-cols-3" :
                  v.market.ral.rows.length === 2 ? "md:grid-cols-2" :
                  ""
                } gap-5`}>
                  {v.market.ral.rows.map((r) => (
                    <div
                      key={r.k}
                      className="rounded-xl p-6 flex flex-col h-full"
                      style={{ background: "rgba(255,255,255,0.04)" }}
                    >
                      <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">{r.k}</p>
                      <p className="font-display text-2xl text-lovac-accent mt-4 leading-tight">{r.v}</p>
                      <p className="text-sm text-on-dark-muted mt-3 leading-relaxed flex-1">{r.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3. PROBLEMI */}
      {v.problemi && (
        <section className="py-24 bg-lovac-2">
          <div className="container-lovac">
            <div className="max-w-3xl">
              <p className="badge-lovac">Il problema</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
                {v.problemi.title}
              </h2>
            </div>

            <div className="mt-14 grid lg:grid-cols-3 gap-6">
              {v.problemi.blocks.map((b, i) => {
                const Icon = problemIcons[i] || Briefcase;
                return (
                  <div
                    key={b.title}
                    className="rounded-xl p-8 flex flex-col h-full"
                    style={{
                      background: "var(--lovac-bg)",
                      border: "0.5px solid var(--lovac-border)",
                    }}
                  >
                    <div className="flex items-center justify-between mb-6">
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center"
                        style={{ background: "rgba(0,196,140,0.12)" }}>
                        <Icon size={18} className="text-lovac-accent" />
                      </div>
                      <span className="font-mono text-xs text-lovac-muted">0{i + 1}</span>
                    </div>
                    <h3 className="font-display text-xl text-lovac-dark leading-snug">{b.title}</h3>
                    <p className="text-sm text-lovac-muted mt-4 leading-relaxed whitespace-pre-line">
                      {b.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* 4. METODO */}
      {v.metodo && (
        <section className="py-24 bg-lovac">
          <div className="container-lovac grid lg:grid-cols-[1.4fr_1fr] gap-14 items-start">
            <div>
              <p className="badge-lovac">Come lavoriamo</p>
              <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
                {v.metodo.title}
              </h2>
              <div className="mt-7 space-y-5 text-base text-lovac-dark leading-[1.7] max-w-2xl">
                {v.metodo.paragraphs.map((p, i) => (
                  <p key={i} className={i === v.metodo.paragraphs.length - 1 ? "font-display text-xl text-lovac-dark" : ""}>
                    {p}
                  </p>
                ))}
              </div>
            </div>

            <div className="card-dark lg:sticky lg:top-28">
              <div className="flex items-center gap-3 mb-5">
                <Building2 size={18} className="text-lovac-accent" />
                <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Ruoli coperti</p>
              </div>
              <ul className="space-y-3">
                {v.metodo.ruoli_coperti.map((r) => (
                  <li key={r} className="flex items-start gap-3 text-sm text-on-dark">
                    <Check size={16} className="text-lovac-accent shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* 5. CASI STUDIO */}
      {relatedCases.length > 0 && (
        <section className="py-24 bg-lovac-2">
          <div className="container-lovac">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-12">
              <div className="max-w-2xl">
                <p className="badge-lovac">Casi studio</p>
                <h2 className="font-display text-4xl sm:text-5xl mt-4 text-lovac-dark leading-tight">
                  Aziende che avevano il tuo stesso problema.
                </h2>
              </div>
              <Link to="/casi-studio" className="link-underline text-sm">
                Vedi tutti i casi studio →
              </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {relatedCases.map((c) => (
                <CaseCard key={c.slug} c={c} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 6. HIRING SCAN TEASER */}
      {v.hiring_scan && (
        <section className="py-20 bg-lovac-dark text-on-dark grain relative overflow-hidden">
          <div className="container-lovac px-6 relative z-10 grid lg:grid-cols-[1.5fr_auto] gap-10 items-center">
            <div>
              <span className="badge-on-dark">Hiring Scan</span>
              <h2 className="font-display text-3xl sm:text-4xl mt-4 text-on-dark leading-tight">
                {v.hiring_scan.title}
              </h2>
              <p className="text-on-dark-muted mt-4 max-w-xl leading-relaxed">{v.hiring_scan.subtitle}</p>
            </div>
            <Link to="/hiring-scan" className="btn-primary">
              Prova l'Hiring Scan <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      )}

      {/* 7. CTA FINALE */}
      <section className="py-24 bg-lovac">
        <div className="container-narrow text-center">
          <p className="badge-lovac">Prenota una call</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-6 text-lovac-dark leading-tight">
            {v.cta_titolo || "Hai una posizione aperta da troppo tempo?"}
          </h2>
          <p className="text-lovac-muted mt-5">
            Parlacene in una call di 30 minuti. Se non facciamo al caso tuo, te lo diremo.
          </p>
          <div className="mt-10">
            <Link to="/contatti" className="btn-primary" data-testid={VERTICAL.cta}>
              Prenota una call <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* =========================================
   LAYOUT LEGACY, verticali ancora senza copy esteso
   ========================================= */
function LegacyVertical({ v }) {
  return (
    <>
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 pt-24 pb-24 relative z-10">
          <span className="badge-on-dark">{v.hero_eyebrow}</span>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 text-on-dark max-w-4xl">{v.title}</h1>
          <p className="text-on-dark-muted text-lg mt-6 max-w-2xl">{v.subtitle}</p>

          <div className="grid sm:grid-cols-3 gap-5 mt-14">
            {v.metrics.map((m) => (
              <div key={m.l} className="card-dark">
                <p className="font-display text-3xl text-on-dark">{m.v}</p>
                <p className="text-sm text-on-dark-muted mt-3">{m.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-lovac">
        <div className="container-lovac grid lg:grid-cols-2 gap-12">
          <div>
            <p className="badge-lovac">Profili che gestiamo</p>
            <h2 className="font-display text-4xl mt-4 text-lovac-dark">Cosa cerchiamo, in concreto.</h2>
            <ul className="mt-8 space-y-4">
              {v.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-base text-lovac-dark">
                  <Check size={18} className="text-lovac-accent shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-dark">
            <p className="text-xs uppercase tracking-wider text-on-dark-muted">Settori serviti</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {v.sectors.map((s) => (
                <span key={s} className="text-sm text-on-dark border border-lovac-dark px-3 py-1.5 rounded-full">{s}</span>
              ))}
            </div>
            <div className="mt-10 pt-8 border-t border-lovac-dark">
              <p className="text-sm text-on-dark-muted">
                Hai un ruolo {v.short.toLowerCase()} aperto? Trenta minuti di call con un recruiter del verticale.
              </p>
              <Link to="/contatti" className="btn-primary mt-6 inline-flex" data-testid={VERTICAL.cta}>
                Prenota una call <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-lovac-2">
        <div className="container-narrow text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-lovac-dark">{v.cta_title}</h2>
          <p className="text-lovac-muted mt-4">Capiamo il ruolo. Ti diciamo se possiamo aiutarti, e in quanto tempo.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/contatti" className="btn-primary">Prenota una call <ArrowRight size={16} /></Link>
            <Link to="/hiring-scan" className="btn-outline-light">Prova l'Hiring Scan</Link>
          </div>
        </div>
      </section>
    </>
  );
}
